import type { SurveyAttribute, SurveyScoresByQuestionXAttributes } from '@engagement-web-app/common';
import classNames from 'classnames';
import { ChevronsLeftRightEllipsis } from 'lucide-react';
import { type RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { ErrorOverlay } from '@/components/ErrorOverlay';
import { Flex } from '@/components/Flex';
import { Icon } from '@/components/Icon';
import { Pagination } from '@/components/Pagination';
import { Pill } from '@/components/Pill';
import { Section, SectionHeader } from '@/components/Section';
import { Select } from '@/components/Select';
import { Skeleton } from '@/components/Skeleton';
import { AttributeFilter, AttributeFilterSkeleton } from '@/components/surveys/categories/AttributeFilter';
import { ComparisonTable } from '@/components/surveys/common/ComparisonTable';
import { Table } from '@/components/Table';
import { TextButton } from '@/components/TextButton';
import { useTopRef } from '@/hooks';
import { fitTableToPageInPrint, hiddenInPrint, wrapTextInPrint } from '@/styles/print.css';
import type { ScoreKey, SortOrder } from '@/utils';
import * as styles from './SurveyQuestionsComparisonTable.css';

type SurveyQuestionsComparisonTableProps = {
    /**
     * `null` until the first fetch for the current filter resolves. The underlying query
     * keeps the previous filter's data around during a refetch, so this doesn't go back
     * to `null` on a filter change — see the note on `QuestionScoresTable`.
     */
    scores: SurveyScoresByQuestionXAttributes | null;
    /** `null` when there's no previous survey to compare against, or its own filtered fetch hasn't resolved. */
    previousScores: SurveyScoresByQuestionXAttributes | null;
    /** `null` while still loading. */
    attributeSettings: SurveyAttribute[] | null;
    selectedAttributeOptionIds: string[];
    onSelectedAttributeOptionIdsChange: (ids: string[]) => void;
    /**
     * Shows a per-question button that surfaces the question's correlations.
     * Omit to hide the button — the correlations endpoint is system-only, so
     * the client-facing page doesn't pass this.
     */
    onShowCorrelations?: (questionId: string) => void;
    /** True while `scores`/`previousScores` are being (re)fetched for the current filter. */
    isFetching: boolean;
    isError: boolean;
    onRetry: () => void;
    /**
     * Splits the questions across pages. Turn it off where every row has to be
     * present at once — printing carries only what is rendered, so a paginated
     * table would reach the paper holding a single page of questions.
     */
    withPagination?: boolean;
    /**
     * Brings this question into view once `scores` is available: switches to the page
     * holding it in the current order, clears the category filter when the question
     * falls outside it, scrolls its row into view, and briefly highlights it. Runs once
     * per distinct value; an id not found in `scores` is ignored.
     */
    focusedQuestionId?: string;
};

type QuestionScores = SurveyScoresByQuestionXAttributes['categories'][number]['questions'][number];

type SortKey = ScoreKey | 'favorable';

const PAGE_SIZE = 10;

/** How long a focused question's row stays highlighted. Matches the `highlightedRow` animation. */
const HIGHLIGHT_DURATION_MS = 2000;

// Pure composition: `attributeSettings` and `scores` are independent loading states
// (the former loads once, the latter reloads on every filter change), so each child
// buffers its own rather than one big component juggling both. The category select
// doesn't gate on `scores` either — like `attributeSettings`, it's purely for further
// filtering, and works fine with no options while `scores` is still loading.
export function SurveyQuestionsComparisonTable({
    scores,
    previousScores,
    attributeSettings,
    selectedAttributeOptionIds,
    onSelectedAttributeOptionIdsChange,
    onShowCorrelations,
    isFetching,
    isError,
    onRetry,
    withPagination = true,
    focusedQuestionId,
}: SurveyQuestionsComparisonTableProps) {
    const [selectedCategoryId, setSelectedCategoryId] = useState('');
    const { topRef, TopRef } = useTopRef();

    if (isError) return <ErrorOverlay active onRetry={onRetry} message="設問別スコアの取得に失敗しました。" />;

    return (
        <Section>
            <SectionHeader
                title="設問別スコア"
                description="設問ごとの DI・偏差値・回答割合を一覧し、前回サーベイからの差を併記します。カテゴリーと属性での絞り込み、各列での並べ替えができます。絞り込んだ結果の回答者が 3 人未満の場合、スコアは表示されません。"
            />

            <Flex direction="column" gap="6">
                <div>
                    <Select value={selectedCategoryId} onChange={(e) => setSelectedCategoryId(e.target.value)}>
                        <option value="">すべてのカテゴリー</option>
                        {scores?.categories.map((category) => (
                            <option key={category.id} value={category.id}>
                                {category.name}
                            </option>
                        ))}
                    </Select>
                </div>

                <TopRef />

                {attributeSettings ? (
                    <AttributeFilterSection
                        attributeSettings={attributeSettings}
                        selectedAttributeOptionIds={selectedAttributeOptionIds}
                        onSelectedAttributeOptionIdsChange={onSelectedAttributeOptionIdsChange}
                    />
                ) : (
                    <AttributeFilterSectionSkeleton />
                )}

                {scores ? (
                    <QuestionScoresTable
                        scores={scores}
                        previousScores={previousScores}
                        selectedCategoryId={selectedCategoryId}
                        onSelectedCategoryIdChange={setSelectedCategoryId}
                        onShowCorrelations={onShowCorrelations}
                        isFetching={isFetching}
                        isLayoutSettled={!isFetching && attributeSettings !== null}
                        topRef={topRef}
                        withPagination={withPagination}
                        focusedQuestionId={focusedQuestionId}
                    />
                ) : (
                    // Reached only before the first fetch ever resolves — once `scores` has a value,
                    // the underlying query keeps it around during a refetch, so this never flashes
                    // back in on a filter change.
                    <QuestionScoresTableSkeleton />
                )}
            </Flex>
        </Section>
    );
}

type AttributeFilterSectionProps = {
    attributeSettings: SurveyAttribute[];
    selectedAttributeOptionIds: string[];
    onSelectedAttributeOptionIdsChange: (ids: string[]) => void;
};

function AttributeFilterSection({
    attributeSettings,
    selectedAttributeOptionIds,
    onSelectedAttributeOptionIdsChange,
}: AttributeFilterSectionProps) {
    const selectedOptions = useMemo(
        () =>
            attributeSettings.flatMap((setting) =>
                setting.options
                    .filter((o) => selectedAttributeOptionIds.includes(o.id))
                    .map((o) => ({ ...o, settingName: setting.name }))
            ),
        [attributeSettings, selectedAttributeOptionIds]
    );

    function handleSelectionChange(options: SurveyAttribute['options']) {
        onSelectedAttributeOptionIdsChange(options.map((o) => o.id));
    }

    return (
        <Flex direction="column" gap="4" align="start">
            <AttributeFilter
                attributes={attributeSettings}
                selectedOptionIds={selectedAttributeOptionIds}
                onSelectionChange={handleSelectionChange}
            />

            {selectedOptions.length > 0 && (
                <Flex gap="2" wrap="wrap">
                    {selectedOptions.map((option) => (
                        <Pill variant="quinary" key={option.id}>
                            {option.settingName}：{option.name}
                        </Pill>
                    ))}
                </Flex>
            )}
        </Flex>
    );
}

function AttributeFilterSectionSkeleton() {
    return (
        <Flex direction="column" gap="4" align="start">
            <AttributeFilterSkeleton />
        </Flex>
    );
}

type QuestionScoresTableProps = {
    scores: SurveyScoresByQuestionXAttributes;
    /** `null` when there's no previous survey to compare against. Not a loading state. */
    previousScores: SurveyScoresByQuestionXAttributes | null;
    selectedCategoryId: string;
    onSelectedCategoryIdChange: (categoryId: string) => void;
    onShowCorrelations?: (questionId: string) => void;
    /** True while `scores`/`previousScores` are being (re)fetched for the current filter. */
    isFetching: boolean;
    /** True once nothing above or inside the table is still loading, so its rows stay where they are. */
    isLayoutSettled: boolean;
    topRef: ReturnType<typeof useTopRef>['topRef'];
    withPagination: boolean;
    focusedQuestionId?: string;
};

/** The questions of the given category (all of them for `''`), in the given sort order. */
function orderQuestions(
    scores: SurveyScoresByQuestionXAttributes,
    categoryId: string,
    sortKey: SortKey | null,
    sortDir: SortOrder | null
): QuestionScores[] {
    const categoryQuestions = categoryId
        ? (scores.categories.find((c) => c.id === categoryId)?.questions ?? [])
        : scores.categories.flatMap((c) => c.questions);

    if (sortKey === null || sortDir === null) return categoryQuestions;

    return [...categoryQuestions].sort((a, b) => {
        let aVal: number;
        let bVal: number;

        if (sortKey === 'favorable') {
            aVal = a.distribution?.favorable ?? -Infinity;
            bVal = b.distribution?.favorable ?? -Infinity;
        } else {
            aVal = a[sortKey] ?? -Infinity;
            bVal = b[sortKey] ?? -Infinity;
        }

        return sortDir === 'desc' ? bVal - aVal : aVal - bVal;
    });
}

// The row count, table shell, and pagination are lifted here rather than duplicated
// across both halves: the column labels are static, and sorting/pagination are driven
// by state this component already owns.
function QuestionScoresTable({
    scores,
    previousScores,
    selectedCategoryId,
    onSelectedCategoryIdChange,
    onShowCorrelations,
    isFetching,
    isLayoutSettled,
    topRef,
    withPagination,
    focusedQuestionId,
}: QuestionScoresTableProps) {
    const [sortKey, setSortKey] = useState<SortKey | null>(null);
    const [sortDir, setSortDir] = useState<SortOrder | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageCategoryId, setPageCategoryId] = useState(selectedCategoryId);
    const [highlightedQuestionId, setHighlightedQuestionId] = useState<string | null>(null);
    const handledFocusedQuestionIdRef = useRef<string | null>(null);
    const highlightedRowRef = useRef<HTMLTableRowElement>(null);

    /*
     * The category select lives in the parent, so its change can't reset pagination
     * imperatively. Tracking the category the page belongs to (rather than resetting in
     * an effect) lets the focus below switch category and page in the same update
     * without the reset overriding the page it picked.
     */
    if (pageCategoryId !== selectedCategoryId) {
        setPageCategoryId(selectedCategoryId);
        setCurrentPage(1);
    }

    useEffect(() => {
        if (!focusedQuestionId || handledFocusedQuestionIdRef.current === focusedQuestionId) return;
        handledFocusedQuestionIdRef.current = focusedQuestionId;

        let categoryId = selectedCategoryId;
        let index = orderQuestions(scores, categoryId, sortKey, sortDir).findIndex((q) => q.id === focusedQuestionId);
        if (index === -1 && categoryId !== '') {
            categoryId = '';
            index = orderQuestions(scores, categoryId, sortKey, sortDir).findIndex((q) => q.id === focusedQuestionId);
        }
        if (index === -1) return;

        if (categoryId !== selectedCategoryId) onSelectedCategoryIdChange(categoryId);
        setPageCategoryId(categoryId);
        setCurrentPage(Math.floor(index / PAGE_SIZE) + 1);
        setHighlightedQuestionId(focusedQuestionId);
    }, [focusedQuestionId, scores, selectedCategoryId, sortKey, sortDir, onSelectedCategoryIdChange]);

    useEffect(() => {
        if (highlightedQuestionId === null || !isLayoutSettled) return;

        /*
         * Sections above the table (rankings, the attribute filter) and the table's own
         * overlay change height while their data loads, which would push the row away from
         * a position scrolled to earlier. Waiting for a frame after everything has loaded
         * scrolls against the final layout.
         */
        const frame = requestAnimationFrame(() =>
            highlightedRowRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
        );
        const timer = setTimeout(() => setHighlightedQuestionId(null), HIGHLIGHT_DURATION_MS);
        return () => {
            cancelAnimationFrame(frame);
            clearTimeout(timer);
        };
    }, [highlightedQuestionId, isLayoutSettled]);

    const previousQuestionMap = useMemo<Map<string, QuestionScores>>(() => {
        if (!previousScores) return new Map();
        return new Map(previousScores.categories.flatMap((c) => c.questions).map((q) => [q.id, q]));
    }, [previousScores]);

    const questions = useMemo<QuestionScores[]>(
        () => orderQuestions(scores, selectedCategoryId, sortKey, sortDir),
        [scores, selectedCategoryId, sortKey, sortDir]
    );

    function handleSort(key: SortKey) {
        if (sortKey !== key) {
            setSortKey(key);
            setSortDir('desc');
        } else if (sortDir === 'desc') {
            setSortDir('asc');
        } else {
            setSortKey(null);
            setSortDir(null);
        }
        setCurrentPage(1);
    }

    function directionFor(key: SortKey) {
        return sortKey === key ? sortDir : null;
    }

    function handlePageChange(page: number) {
        setCurrentPage(page);
        topRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }

    const lastPage = Math.max(1, Math.ceil(questions.length / PAGE_SIZE));
    const safeCurrentPage = Math.min(currentPage, lastPage);
    const visibleQuestions = withPagination
        ? questions.slice((safeCurrentPage - 1) * PAGE_SIZE, safeCurrentPage * PAGE_SIZE)
        : questions;

    return (
        <>
            <Table.RowCount label="設問数" count={questions.length} unit="問" />

            <Skeleton.Overlay active={isFetching}>
                <Table.Root className={fitTableToPageInPrint}>
                    <Table.Header>
                        <Table.HeaderCell>設問</Table.HeaderCell>
                        <Table.HeaderCell>
                            <Table.SortButton onClick={() => handleSort('di')} direction={directionFor('di')}>
                                DI
                            </Table.SortButton>
                        </Table.HeaderCell>
                        <Table.HeaderCell>
                            <Table.SortButton
                                onClick={() => handleSort('deviation')}
                                direction={directionFor('deviation')}
                            >
                                偏差値
                            </Table.SortButton>
                        </Table.HeaderCell>
                        <Table.HeaderCell>
                            <Table.SortButton
                                onClick={() => handleSort('favorable')}
                                direction={directionFor('favorable')}
                            >
                                回答割合
                            </Table.SortButton>
                        </Table.HeaderCell>
                    </Table.Header>

                    <Table.Body>
                        <QuestionRows
                            questions={visibleQuestions}
                            previousQuestionMap={previousQuestionMap}
                            onShowCorrelations={onShowCorrelations}
                            highlightedQuestionId={isLayoutSettled ? highlightedQuestionId : null}
                            highlightedRowRef={highlightedRowRef}
                        />
                    </Table.Body>
                </Table.Root>
            </Skeleton.Overlay>

            {withPagination && (
                <Flex flex justify="center">
                    <Pagination currentPage={safeCurrentPage} lastPage={lastPage} onPageChange={handlePageChange} />
                </Flex>
            )}
        </>
    );
}

function QuestionScoresTableSkeleton() {
    return (
        <>
            <Skeleton.Line width={120} height="xs" />
            <Table.Skeleton columns={4} rows={PAGE_SIZE} />
        </>
    );
}

/**
 * A placeholder for the whole card, for the page's initial load — distinct from the
 * partial loading the component handles internally once it's on screen.
 */
export function SurveyQuestionsComparisonTableSkeleton() {
    return (
        <Section>
            <Flex direction="column" gap="6">
                <Flex direction="column" gap="1">
                    <Skeleton.Line width="40%" height="xl" />
                    <Skeleton.Line width="70%" height="sm" />
                </Flex>

                <Skeleton.Box width={120} height={42} />

                <AttributeFilterSectionSkeleton />

                <QuestionScoresTableSkeleton />
            </Flex>
        </Section>
    );
}

type QuestionRowsProps = {
    questions: QuestionScores[];
    previousQuestionMap: Map<string, QuestionScores>;
    onShowCorrelations?: (questionId: string) => void;
    highlightedQuestionId: string | null;
    highlightedRowRef: RefObject<HTMLTableRowElement | null>;
};

function QuestionRows({
    questions,
    previousQuestionMap,
    onShowCorrelations,
    highlightedQuestionId,
    highlightedRowRef,
}: QuestionRowsProps) {
    return (
        <>
            {questions.map((question) => {
                const previous = question.previousQuestionId
                    ? previousQuestionMap.get(question.previousQuestionId)
                    : null;
                const isHighlighted = question.id === highlightedQuestionId;

                return (
                    <Table.Row
                        key={question.id}
                        ref={isHighlighted ? highlightedRowRef : undefined}
                        className={classNames({ [styles.highlightedRow]: isHighlighted })}
                    >
                        <Table.Cell>
                            <Flex direction="column" align="start" gap="2">
                                <span
                                    title={`${question.name} ${question.text}`}
                                    className={classNames(styles.questionText, wrapTextInPrint)}
                                >
                                    <span className={styles.questionNumber}>{question.name}</span>
                                    {question.text}
                                </span>
                                <Flex align="center" gap="2">
                                    <Pill variant="quinary" weight="bold" size="small">
                                        {question.categoryName}
                                    </Pill>
                                    <div className={styles.separator}>&nbsp;</div>
                                    {onShowCorrelations && (
                                        <TextButton
                                            className={hiddenInPrint}
                                            onClick={() => onShowCorrelations(question.id)}
                                        >
                                            <Icon icon={ChevronsLeftRightEllipsis} size="sm" />
                                            相関設問を見る
                                        </TextButton>
                                    )}
                                </Flex>
                            </Flex>
                        </Table.Cell>
                        <Table.Cell>
                            <ComparisonTable.ScoreCell
                                value={question.di}
                                previousValue={previous?.di ?? null}
                                unit={question.questionType === 'number' ? '点' : undefined}
                            />
                        </Table.Cell>
                        <Table.Cell>
                            <ComparisonTable.ScoreCell
                                value={question.deviation}
                                previousValue={previous?.deviation ?? null}
                            />
                        </Table.Cell>
                        <Table.Cell>
                            {question.distribution && (
                                <ComparisonTable.DistributionCell distribution={question.distribution} />
                            )}
                        </Table.Cell>
                    </Table.Row>
                );
            })}
        </>
    );
}
